import { billers as seedBillers } from '@/mocks/billers';
import type { Biller } from '@/types/portal';

/**
 * Local persistence for the billers a finance user pays.
 *
 * The seed directory ships the agency's common billers with a few already
 * pinned; the pinned state (and any edits) is stored on top of it in
 * localStorage so biller favourites survive between sessions. Falls back to the
 * seed set on first load or when storage is unavailable.
 */
const STORAGE_KEY = 'twiva.billers.v1';

/** Read the persisted billers, falling back to the seed set. */
export function loadBillers(): Biller[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedBillers;
    const parsed = JSON.parse(raw) as Biller[];
    if (!Array.isArray(parsed) || parsed.length === 0) return seedBillers;
    return parsed;
  } catch {
    return seedBillers;
  }
}

/** Persist the billers; degrades quietly if storage is blocked. */
export function persistBillers(billers: Biller[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(billers));
  } catch {
    // Storage unavailable (private mode) — the session keeps the in-memory copy.
  }
}

/** Flip a biller's pinned state, returning a new list. */
export function toggleBillerFavourite(billers: Biller[], id: string): Biller[] {
  return billers.map((biller) =>
    biller.id === id ? { ...biller, favourite: !biller.favourite } : biller,
  );
}